<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("issue_report.form.title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <!-- spec 2026-10-07-bo-form-draft-clear-design.md §3 F2 / §4 A7:
                         leftmost of the header group; "my reports" is md so both
                         buttons are the same height (FormClearButton is md) -->
                    <form-clear-button class="mr-2" :disabled="submitting" @click="clearForm" />
                    <base-button size="md" type="neutral" @click="goList">
                        <i class="fa fa-list"></i>
                        {{ $t("issue_report.form.my_reports") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card">
                <template slot="header">
                    <h3 class="mb-0">{{ $t("issue_report.form.title") }}</h3>
                </template>

                <el-form
                    ref="form"
                    :model="form"
                    label-position="top"
                    @submit.native.prevent
                >
                    <div class="row">
                        <!-- Kind -->
                        <div class="col-md-6 mb-2">
                            <label class="form-control-label">{{ $t("issue_report.form.kind") }}</label>
                            <div>
                                <el-radio-group v-model="form.kind">
                                    <el-radio-button label="bug">
                                        <i class="fa fa-bug"></i>
                                        {{ $t("issue_report.kind.bug") }}
                                    </el-radio-button>
                                    <el-radio-button label="feature">
                                        <i class="fa fa-lightbulb"></i>
                                        {{ $t("issue_report.kind.feature") }}
                                    </el-radio-button>
                                </el-radio-group>
                            </div>
                        </div>

                        <!-- Component -->
                        <div class="col-md-6 mb-2">
                            <label class="form-control-label">
                                {{ $t("issue_report.form.component") }}
                                <span class="text-danger">*</span>
                            </label>
                            <el-select v-model="form.component" style="width: 100%">
                                <el-option
                                    v-for="c in componentOptions"
                                    :key="c"
                                    :value="c"
                                    :label="$t('issue_report.component.' + c) + ' — ' + $t('issue_report.component_desc.' + c)"
                                >
                                    <span style="font-weight: 600">{{ $t("issue_report.component." + c) }}</span>
                                    <span class="text-muted ml-2">{{ $t("issue_report.component_desc." + c) }}</span>
                                </el-option>
                            </el-select>
                        </div>
                    </div>

                    <!-- Title -->
                    <div class="row">
                        <div class="col-12 mb-2">
                            <label class="form-control-label">
                                {{ $t("issue_report.form.subject") }}
                                <span class="text-danger">*</span>
                            </label>
                            <el-input
                                v-model="form.title"
                                :placeholder="$t('issue_report.form.subject_placeholder')"
                                maxlength="200"
                                show-word-limit
                            ></el-input>
                        </div>
                    </div>

                    <!-- Description -->
                    <div class="row">
                        <div class="col-12 mb-2">
                            <label class="form-control-label">
                                {{ $t("issue_report.form.description") }}
                                <span class="text-danger">*</span>
                            </label>
                            <el-input
                                v-model="form.body"
                                type="textarea"
                                :rows="6"
                                :placeholder="$t('issue_report.form.description_placeholder')"
                            ></el-input>
                        </div>
                    </div>

                    <div class="row">
                        <!-- Severity (optional) -->
                        <div class="col-md-6 mb-2">
                            <label class="form-control-label">{{ $t("issue_report.form.severity") }}</label>
                            <el-select v-model="form.severity" clearable style="width: 100%">
                                <el-option
                                    v-for="s in severityOptions"
                                    :key="s"
                                    :value="s"
                                    :label="$t('issue_report.severity.' + s)"
                                ></el-option>
                            </el-select>
                        </div>

                        <!-- Screenshots (optional, up to 10) -->
                        <div class="col-md-6 mb-2">
                            <label class="form-control-label">
                                {{ $t("issue_report.form.attachments") }}
                                <span class="text-muted">({{ attachments.length }}/{{ maxFiles }})</span>
                            </label>
                            <div>
                                <input
                                    ref="screenshotInput"
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/gif"
                                    multiple
                                    style="display: none"
                                    @change="onFilesChange"
                                />
                                <base-button
                                    size="sm"
                                    type="secondary"
                                    :disabled="uploading || attachments.length >= maxFiles"
                                    @click="pickScreenshot"
                                >
                                    <i class="fa fa-upload"></i>
                                    {{ uploading ? $t("issue_report.form.uploading") : $t("issue_report.form.add_attachments") }}
                                </base-button>
                                <span class="text-muted small ml-2">{{ $t("issue_report.form.attach_limit") }}</span>

                                <div v-if="attachments.length" class="issue-thumb-grid mt-2">
                                    <div
                                        v-for="a in attachments"
                                        :key="a.id"
                                        class="issue-thumb-item"
                                    >
                                        <a
                                            v-if="a.status === 'done'"
                                            :href="a.url"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img :src="a.url" class="issue-screenshot-thumb" />
                                        </a>
                                        <div v-else class="issue-thumb-placeholder">
                                            <i
                                                v-if="a.status === 'error'"
                                                class="fa fa-exclamation-triangle text-danger"
                                            ></i>
                                            <template v-else>
                                                <i class="fa fa-spinner fa-spin"></i>
                                                <div class="issue-thumb-progress">{{ a.progress }}%</div>
                                            </template>
                                        </div>
                                        <button
                                            type="button"
                                            class="issue-thumb-remove"
                                            :title="$t('issue_report.form.remove')"
                                            @click="removeAttachment(a.id)"
                                        >
                                            <i class="fa fa-times"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row mt-3">
                        <div class="col-12 text-right">
                            <base-button type="secondary" @click="goList">
                                {{ $t("issue_report.form.cancel") }}
                            </base-button>
                            <base-button type="primary" :disabled="submitting" @click="submit">
                                <i class="fa fa-paper-plane"></i>
                                {{ $t("issue_report.form.submit") }}
                            </base-button>
                        </div>
                    </div>
                </el-form>
            </card>
        </div>
    </div>
</template>

<script>
import { Form, Input, Select, Option, RadioGroup, RadioButton } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { mapActions } from "vuex";
import { IssueReportService } from "@/resource";
import formDraft from "@/mixins/formDraft";
import FormClearButton from "@/components/FormClearButton.vue";

export default {
    name: "IssueReportForm",
    mixins: [formDraft],
    components: {
        FormClearButton,
        RouteBreadCrumb,
        [Form.name]: Form,
        [Input.name]: Input,
        [Select.name]: Select,
        [Option.name]: Option,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton
    },
    data() {
        return {
            submitting: false,
            maxFiles: 10,
            allowedTypes: ["image/png", "image/jpeg", "image/webp", "image/gif"],
            // Each entry: { id, name, status: 'uploading'|'done'|'error', progress, url }
            attachments: [],
            // Was `_uid`. Vue 2 does not proxy data keys beginning with `_`
            // onto the instance (isReserved), so `this._uid` was not reading
            // this counter at all — it was reading, and `++` was mutating,
            // Vue's own internal component uid. eslint flagged it as
            // vue/no-reserved-keys; the rename is the actual fix.
            attachmentSeq: 0,
            componentOptions: ["MEMBER", "APP", "WEB", "BO"],
            severityOptions: ["low", "medium", "high"],
            form: {
                title: "",
                body: "",
                component: "BO",
                kind: "bug",
                severity: ""
            }
        };
    },
    computed: {
        // True while any queued attachment is still uploading.
        uploading() {
            return this.attachments.some(a => a.status === "uploading");
        }
    },
    methods: {
        ...mapActions({
            CreateIssueReport: "IssueReport/Create"
        }),
        // formDraft contract (spec 2026-10-07-bo-form-draft-clear-design.md
        // §3 F1, §4 A7): kind / component / subject / description / severity
        // back to the data() defaults and every attachment (done, uploading
        // or failed) dropped, plus the hidden <input type=file>.
        // attachmentSeq is NOT rewound: an upload still in flight keeps its
        // id, and a new attachment must never reuse it. submitting is left to
        // submit()'s finally (afterSaveReset runs before it).
        resetForm() {
            const fresh = this.$options.data.call(this);
            this.form = fresh.form;
            this.attachments = [];
            const input = this.$refs.screenshotInput;
            if (input) input.value = "";
        },
        pickScreenshot() {
            this.$refs.screenshotInput.value = "";
            this.$refs.screenshotInput.click();
        },
        // Multi-file select: validate each, enforce the 10-image cap, then queue
        // each accepted file through its own presign + PUT upload.
        onFilesChange(e) {
            const files = Array.from((e.target && e.target.files) || []);
            e.target.value = "";
            if (!files.length) return;

            const maxBytes = 5 * 1024 * 1024;
            let remaining = this.maxFiles - this.attachments.length;
            let limitHit = false;

            for (const file of files) {
                if (remaining <= 0) {
                    limitHit = true;
                    break;
                }
                if (this.allowedTypes.indexOf(file.type) === -1) {
                    this.$notify({
                        type: "warning",
                        message: this.$t("issue_report.form.file_type_invalid") + ": " + file.name
                    });
                    continue;
                }
                if (file.size > maxBytes) {
                    this.$notify({
                        type: "danger",
                        message: this.$t("issue_report.form.file_too_large") + ": " + file.name
                    });
                    continue;
                }
                remaining -= 1;
                this.uploadFile(file);
            }

            if (limitHit) {
                this.$notify({
                    type: "warning",
                    message: this.$t("issue_report.form.attach_limit_reached")
                });
            }
        },
        // Upload flow per file: presign (issue-report endpoint, open to all
        // staff) -> PUT to S3 with progress -> keep the returned CDN URL.
        async uploadFile(file) {
            const item = {
                id: ++this.attachmentSeq,
                name: file.name,
                status: "uploading",
                progress: 0,
                url: ""
            };
            this.attachments.push(item);
            try {
                const presignRes = await IssueReportService.Presign({
                    file_name: file.name,
                    content_type: file.type
                });
                const { upload_url, cdn_url } = presignRes.data;
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open("PUT", upload_url);
                    xhr.setRequestHeader("Content-Type", file.type);
                    xhr.upload.onprogress = ev => {
                        if (ev.lengthComputable) {
                            item.progress = Math.round((ev.loaded / ev.total) * 100);
                        }
                    };
                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) resolve();
                        else reject(new Error("Upload failed: " + xhr.status));
                    };
                    xhr.onerror = () => reject(new Error("Network error"));
                    xhr.send(file);
                });
                item.url = cdn_url;
                item.status = "done";
                item.progress = 100;
            } catch (err) {
                item.status = "error";
                const detail = err && err.message ? ": " + err.message : "";
                this.$notify({
                    type: "danger",
                    message: this.$t("issue_report.form.upload_failed") + " (" + item.name + ")" + detail
                });
                // Drop the failed placeholder so it never blocks the limit/submit.
                this.removeAttachment(item.id);
            }
        },
        removeAttachment(id) {
            const idx = this.attachments.findIndex(a => a.id === id);
            if (idx !== -1) this.attachments.splice(idx, 1);
        },
        validate() {
            if (!this.form.title || !this.form.title.trim()) {
                this.$notify({ type: "warning", message: this.$t("issue_report.form.subject_required") });
                return false;
            }
            if (!this.form.body || !this.form.body.trim()) {
                this.$notify({ type: "warning", message: this.$t("issue_report.form.description_required") });
                return false;
            }
            if (!this.form.component) {
                this.$notify({ type: "warning", message: this.$t("issue_report.form.component_required") });
                return false;
            }
            return true;
        },
        submit() {
            if (!this.validate()) return;
            if (this.uploading) {
                this.$notify({
                    type: "warning",
                    message: this.$t("issue_report.form.wait_upload")
                });
                return;
            }
            this.submitting = true;
            const payload = {
                title: this.form.title.trim(),
                body: this.form.body.trim(),
                component: this.form.component,
                kind: this.form.kind
            };
            if (this.form.severity) payload.severity = this.form.severity;
            const urls = this.attachments
                .filter(a => a.status === "done" && a.url)
                .map(a => a.url);
            if (urls.length) payload.attachment_urls = urls;
            this.CreateIssueReport(payload)
                .then(() => {
                    // Saved: empty the cached tab before leaving (§4 A7).
                    // validate()/upload guards return above; a failed create
                    // goes to the catch and keeps the draft.
                    this.afterSaveReset();
                    this.$notify({
                        type: "success",
                        message: this.$t("issue_report.form.success")
                    });
                    this.$router.push("/admin/issue-reports");
                })
                .catch(err => {
                    console.log("create issue report error", err);
                    this.$notify({
                        type: "danger",
                        message: this.$t("issue_report.form.submit_error")
                    });
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        goList() {
            this.$router.push("/admin/issue-reports");
        }
    }
};
</script>

<style scoped>
.issue-thumb-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.issue-thumb-item {
    position: relative;
    width: 84px;
    height: 84px;
}
.issue-screenshot-thumb {
    width: 84px;
    height: 84px;
    object-fit: cover;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    vertical-align: middle;
}
.issue-thumb-placeholder {
    width: 84px;
    height: 84px;
    border: 1px dashed #d1d5db;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    /* was #8898aa = 2.79:1 on this #f6f9fc tile -> #607286 = 4.68:1 */
    color: #607286;
    background: #f6f9fc;
}
.issue-thumb-progress {
    font-size: 11px;
    margin-top: 4px;
}
.issue-thumb-remove {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 20px;
    height: 20px;
    padding: 0;
    line-height: 18px;
    border-radius: 50%;
    border: none;
    background: #f5365c;
    color: #fff;
    font-size: 11px;
    cursor: pointer;
}
.issue-thumb-remove:hover {
    background: #ec0c38;
}
</style>
